import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  Truck,
  Globe2,
  Factory,
  MapPin,
  ShieldCheck,
  TrendingUp,
  Network,
  Compass,
  Cpu,
  Layers,
  BarChart3,
  AlertTriangle,
  CheckCircle2,
  Users,
  Building2,
  Zap,
  HardHat,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";

const REGIONS = [
  {
    icon: MapPin,
    label: "Polen",
    tag: "Industrial Hub",
    text: "Qualifizierte Fachkräfte, starke industrielle Basis und beste Logistikanbindung an Westeuropa.",
  },
  {
    icon: MapPin,
    label: "Rumänien",
    tag: "Engineering",
    text: "Wachsendes Engineering- und Produktionsökosystem mit attraktiver Kostenstruktur.",
  },
  {
    icon: MapPin,
    label: "Bulgarien",
    tag: "Manufacturing",
    text: "Industrieller Aufstieg mit modernen Produktionsstandorten und EU-Standards.",
  },
  {
    icon: MapPin,
    label: "Tschechien",
    tag: "Precision",
    text: "Etablierte Fertigungskompetenz – insbesondere im Maschinen- und Automobilbau.",
  },
  {
    icon: MapPin,
    label: "Türkei",
    tag: "Strategic Bridge",
    text: "Starke Industriebasis, strategische Lage zwischen Europa, Asien und Nahem Osten, hohe Fertigungskompetenz.",
  },
  {
    icon: MapPin,
    label: "Schwarzmeerregion",
    tag: "New Corridor",
    text: "Verbindung zwischen Europa, Kaukasus, Zentralasien und dem Nahen Osten – neue Handels- und Logistikrouten.",
  },
];

const RISKS = [
  {
    icon: Globe2,
    title: "Geopolitische Spannungen",
    text: "Handelskonflikte, Sanktionen und politische Unsicherheiten erschweren die langfristige Planung.",
  },
  {
    icon: Truck,
    title: "Lange Transportwege",
    text: "Hohe Frachtkosten, längere Vorlaufzeiten und begrenzte Reaktionsfähigkeit auf Marktveränderungen.",
  },
  {
    icon: AlertTriangle,
    title: "Mangelnde Transparenz",
    text: "Begrenzte Einflussmöglichkeiten auf entfernte Lieferanten erhöhen die Gefahr von Produktionsausfällen.",
  },
  {
    icon: Zap,
    title: "Steigende Kundenerwartungen",
    text: "Kürzere Lieferzeiten, höhere Verfügbarkeit und verlässliche Lieferperformance werden zum Standard.",
  },
];

const BENEFITS = [
  "Kürzere Lieferzeiten und höhere Reaktionsgeschwindigkeit",
  "Reduzierte Abhängigkeit von einzelnen Regionen oder Lieferanten",
  "Erhöhte Versorgungssicherheit für kritische Komponenten",
  "Bessere Kontrolle über kritische Wertschöpfungsstufen",
  "Transparenz für Kunden, Investoren und öffentliche Auftraggeber",
  "Nachhaltige Wettbewerbsvorteile durch aktives Risikomanagement",
];

const X10_SUPPORT = [
  {
    icon: BarChart3,
    title: "Strategische Lieferketten-Analyse",
    text: "Bewertung bestehender Lieferantenlandschaften, Identifikation von Abhängigkeiten und kritischen Wertschöpfungsstufen.",
  },
  {
    icon: Compass,
    title: "Nearshoring-Strategie",
    text: "Identifikation und Bewertung von Potenzialen in der Türkei, Polen, Rumänien, Bulgarien, Tschechien und weiteren Standorten.",
  },
  {
    icon: Network,
    title: "Internationales Netzwerk",
    text: "Zugang zu geeigneten Geschäftspartnern, Lieferanten und Produktionsstandorten in den Zielregionen.",
  },
  {
    icon: Cpu,
    title: "Digitalisierung & Steuerung",
    text: "Aufbau von Transparenz, Einführung moderner Analyse- und Steuerungsinstrumente für resiliente Lieferketten.",
  },
];

const UKRAINE_SECTORS = [
  { icon: HardHat, label: "Bau & Infrastruktur" },
  { icon: Factory, label: "Maschinenbau" },
  { icon: Truck, label: "Logistik" },
  { icon: Layers, label: "Engineering" },
  { icon: Zap, label: "Energieversorgung" },
  { icon: Cpu, label: "IT & Digitalisierung" },
  { icon: Users, label: "Personaldienstleistung" },
  { icon: Building2, label: "Industrieausbau" },
];

export default function SupplyChain() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid="supply-chain-page" className="font-body bg-white text-slate-900 min-h-screen">
      <SEO
        title="Supply Chain Resilience & Nearshoring | X10 Management"
        description="Resiliente Lieferketten und gezieltes Nearshoring sind entscheidende Wettbewerbsfaktoren. X10 begleitet Unternehmen bei strategischer Analyse, Diversifizierung und Aufbau widerstandsfähiger Beschaffungs- und Produktionsstrukturen in Europa, der Türkei und der Schwarzmeerregion."
      />
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden bg-[#0A192F] text-white pt-32 lg:pt-36">
        <div className="absolute -top-40 -right-40 w-[560px] h-[560px] rounded-full bg-[#C9A96E]/15 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-40 -left-40 w-[560px] h-[560px] rounded-full bg-[#1E3A8A]/40 blur-3xl pointer-events-none" />
        <div
          className="absolute inset-0 opacity-[0.06] pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
            backgroundSize: "80px 80px",
          }}
        />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <Link
              to="/#services"
              data-testid="sc-back"
              className="inline-flex w-fit items-center gap-2 text-sm text-slate-300 hover:text-white transition-colors mb-8"
            >
              <ArrowLeft size={16} /> Zurück zu Leistungen
            </Link>
            <div>
              <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-extrabold text-[#C9A96E]">
                <span className="w-1.5 h-1.5 rounded-full bg-[#C9A96E]" />
                Discipline 03 · Supply Chain &amp; Nearshoring
              </span>
            </div>
            <h1 className="mt-5 font-heading font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] max-w-4xl">
              Resiliente Lieferketten:
              <span className="block text-[#C9A96E] mt-2">
                Warum Nearshoring zum entscheidenden Wettbewerbsfaktor wird.
              </span>
            </h1>
            <p className="mt-6 text-lg lg:text-xl text-white/85 font-light leading-relaxed max-w-3xl">
              Maximale Kosteneffizienz allein reicht nicht mehr. Versorgungssicherheit, Flexibilität und Resilienz
              werden zum strategischen Kern moderner Unternehmensführung – und Nearshoring zum entscheidenden Hebel.
            </p>

            <div className="mt-10 flex flex-wrap gap-3">
              <Link
                to="/#contact"
                data-testid="sc-cta-contact"
                className="group inline-flex items-center gap-2 px-7 py-4 rounded-full bg-white text-[#0A192F] font-semibold hover:bg-[#C9A96E] hover:text-white transition-all duration-300"
              >
                Lieferketten-Check anfragen
                <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
              </Link>
              <a
                href="#x10-support"
                className="inline-flex items-center gap-2 px-7 py-4 rounded-full border border-white/30 font-semibold hover:bg-white/10 transition-all duration-300"
              >
                Wie X10 begleitet
              </a>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="relative aspect-[4/5] rounded-2xl overflow-hidden ring-1 ring-white/10 max-w-md ml-auto">
              <img
                src="https://images.unsplash.com/photo-1494412519320-aa613dfb7738?auto=format&fit=crop&w=1200&q=80"
                alt="Resiliente internationale Lieferketten und Nearshoring"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-[#0A192F]/80 via-[#0A192F]/30 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 text-white">
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#C9A96E] font-semibold">
                  Strategic Field
                </div>
                <div className="font-heading text-lg font-semibold mt-1 leading-tight">
                  Supply Chain &amp; Nearshoring
                </div>
                <div className="text-sm text-white/80 mt-1">
                  Resilienz · Diversifizierung · Wettbewerbsfähigkeit
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Intro Section */}
      <section className="bg-white py-16 lg:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="text-sm uppercase tracking-[0.25em] font-extrabold text-[#1E3A8A]">Marktverständnis</span>
          <h2 className="mt-4 font-heading text-3xl sm:text-4xl font-bold text-[#0A192F] tracking-tight leading-tight">
            Globale Lieferketten am Wendepunkt.
          </h2>
          <div className="mt-8 space-y-6 text-lg text-slate-700 leading-relaxed">
            <p>
              Die vergangenen Jahre haben deutlich gezeigt, wie anfällig globale Lieferketten sein können. Pandemie,
              geopolitische Spannungen, Handelskonflikte, steigende Transportkosten und regionale Krisen haben
              Unternehmen weltweit vor große Herausforderungen gestellt. Viele Betriebe mussten feststellen, dass
              maximale Kosteneffizienz allein keine ausreichende Strategie mehr ist.
            </p>
            <p>
              Stattdessen rücken <span className="text-[#0A192F] font-medium">Versorgungssicherheit, Flexibilität und
              Resilienz</span> zunehmend in den Mittelpunkt unternehmerischer Entscheidungen. Lange Transportwege,
              begrenzte Einflussmöglichkeiten auf Lieferanten, politische Unsicherheiten und mangelnde Transparenz
              erschweren die Planung – und erhöhen die Gefahr von Produktionsausfällen.
            </p>
          </div>
        </div>
      </section>

      {/* Risks Grid */}
      <section className="bg-[#F3F4F6] py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-sm uppercase tracking-[0.25em] font-extrabold text-[#1E3A8A]">
              Status Quo
            </span>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl font-bold text-[#0A192F] tracking-tight leading-tight">
              Vier Risiken klassischer Lieferketten.
            </h2>
            <p className="mt-5 text-lg text-slate-700 leading-relaxed">
              Wer sich ausschließlich auf weit entfernte Beschaffungsmärkte verlässt, riskiert verzögerte Lieferungen,
              steigende Kosten und Wettbewerbsnachteile. Diese vier Themen prägen die strategische Diskussion in den
              Führungsetagen:
            </p>
          </div>

          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {RISKS.map(({ icon: Icon, title, text }, i) => (
              <motion.div
                key={title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.07 }}
                className="group p-6 rounded-2xl bg-white border border-slate-200 hover:border-[#C9A96E]/60 hover:shadow-[0_0_24px_rgba(201,169,110,0.15)] transition-all duration-300"
              >
                <div className="w-11 h-11 rounded-full bg-[#0A192F]/5 border border-[#C9A96E]/40 flex items-center justify-center">
                  <Icon size={18} className="text-[#0A192F]" />
                </div>
                <div className="mt-4 font-heading text-base font-semibold text-[#0A192F]">{title}</div>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Regions */}
      <section className="bg-white py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-12 mb-12">
            <div className="lg:col-span-7">
              <span className="text-sm uppercase tracking-[0.25em] font-extrabold text-[#1E3A8A]">
                Strategische Standorte
              </span>
              <h2 className="mt-4 font-heading text-3xl sm:text-4xl font-bold text-[#0A192F] tracking-tight leading-tight">
                Europa, Türkei und die Schwarzmeerregion: <span className="text-[#1E3A8A]">Die neuen Knotenpunkte
                resilienter Lieferketten.</span>
              </h2>
              <p className="mt-6 text-lg text-slate-700 leading-relaxed">
                Innerhalb Europas entstehen neue Chancen für Unternehmen, die ihre Beschaffungs- und
                Produktionsstrukturen strategisch neu ausrichten möchten. Besonders interessant: qualifizierte
                Fachkräfte, wettbewerbsfähige Kostenstrukturen, moderne Infrastruktur – und die Nähe zu europäischen
                Absatzmärkten.
              </p>
            </div>
            <div className="lg:col-span-5 flex">
              <div className="self-stretch w-full p-6 rounded-2xl bg-[#0A192F] text-white">
                <div className="text-xs uppercase tracking-[0.25em] font-extrabold text-[#C9A96E]">Insight</div>
                <p className="mt-3 text-slate-200 leading-relaxed">
                  Nearshoring bedeutet <span className="text-white font-medium">nicht zwangsläufig die vollständige
                  Verlagerung</span> bestehender Lieferketten – sondern die gezielte Verlagerung kritischer
                  Komponenten, Vorprodukte oder Produktionskapazitäten näher an die europäischen Märkte.
                </p>
              </div>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {REGIONS.map(({ icon: Icon, label, tag, text }, i) => (
              <motion.div
                key={label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                className="group relative p-6 rounded-2xl bg-[#F3F4F6] border border-slate-200 hover:border-[#1E3A8A]/40 hover:shadow-md transition-all duration-300"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="w-11 h-11 rounded-full bg-[#1E3A8A]/10 border border-[#1E3A8A]/30 flex items-center justify-center">
                    <Icon size={18} className="text-[#1E3A8A]" />
                  </div>
                  <span className="text-[10px] uppercase tracking-[0.2em] font-extrabold text-[#C9A96E]">{tag}</span>
                </div>
                <div className="mt-4 font-heading text-lg font-semibold text-[#0A192F]">{label}</div>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="bg-[#0A192F] text-white py-16 lg:py-24 relative overflow-hidden">
        <div className="absolute -top-40 -right-40 w-[440px] h-[440px] rounded-full bg-[#C9A96E]/10 blur-3xl pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-extrabold text-[#C9A96E]">
              <ShieldCheck size={14} /> Was Nearshoring leistet
            </span>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl font-bold tracking-tight leading-tight">
              Mehr Handlungsspielraum. Kürzere Reaktionszeiten.
              <span className="text-[#C9A96E]"> Stärkere Marktposition.</span>
            </h2>
            <p className="mt-6 text-lg text-slate-300 leading-relaxed">
              Unternehmen, die ihre Lieferketten aktiv managen und flexibel auf Veränderungen reagieren können,
              schaffen nicht nur mehr Sicherheit, sondern häufig auch entscheidende Wettbewerbsvorteile.
            </p>
          </div>
          <div className="lg:col-span-7">
            <ul className="grid sm:grid-cols-2 gap-3">
              {BENEFITS.map((item) => (
                <li
                  key={item}
                  className="flex items-start gap-3 p-4 rounded-xl bg-white/5 border border-white/10"
                >
                  <CheckCircle2 size={18} className="text-[#C9A96E] mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-slate-200 leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Ukraine Reconstruction */}
      <section className="bg-[#F3F4F6] py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative rounded-3xl overflow-hidden bg-white border border-slate-200 shadow-sm">
            <div className="absolute -top-32 -right-32 w-[420px] h-[420px] rounded-full bg-[#1E3A8A]/10 blur-3xl pointer-events-none" />
            <div className="relative p-8 sm:p-12 grid lg:grid-cols-12 gap-10">
              <div className="lg:col-span-7">
                <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-extrabold text-[#C9A96E]">
                  <TrendingUp size={14} /> Forward-Looking Opportunity
                </span>
                <h2 className="mt-4 font-heading text-2xl sm:text-3xl font-bold text-[#0A192F] tracking-tight leading-tight">
                  Wiederaufbau der Ukraine: <span className="text-[#1E3A8A]">Einer der größten Investitionszyklen Europas
                  seit Jahrzehnten.</span>
                </h2>
                <p className="mt-6 text-slate-700 leading-relaxed">
                  Zahlreiche internationale Institutionen, Regierungen und Unternehmen bereiten sich bereits auf die
                  Zeit nach dem Krieg vor. Der Wiederaufbau von Infrastruktur, Industrie, Energieversorgung,
                  Wohnraum, Logistik und Digitalisierung könnte einen der größten Investitionszyklen Europas seit
                  Jahrzehnten auslösen.
                </p>
                <p className="mt-4 text-slate-700 leading-relaxed">
                  Unternehmen, die diese Entwicklungen frühzeitig beobachten und strategisch einordnen, können sich
                  langfristig nachhaltige Wettbewerbsvorteile sichern.
                </p>
              </div>
              <div className="lg:col-span-5">
                <div className="text-xs uppercase tracking-[0.25em] font-extrabold text-[#1E3A8A] mb-4">
                  Relevante Branchen
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {UKRAINE_SECTORS.map(({ icon: Icon, label }) => (
                    <div
                      key={label}
                      className="flex items-center gap-3 p-3 rounded-xl bg-[#F3F4F6] border border-slate-200"
                    >
                      <div className="w-9 h-9 rounded-full bg-white border border-[#C9A96E]/40 flex items-center justify-center flex-shrink-0">
                        <Icon size={15} className="text-[#0A192F]" />
                      </div>
                      <span className="text-sm font-medium text-[#0A192F]">{label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* X10 Support */}
      <section id="x10-support" className="bg-white py-16 lg:py-24 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-sm uppercase tracking-[0.25em] font-extrabold text-[#1E3A8A]">Unser Beitrag</span>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl font-bold text-[#0A192F] tracking-tight leading-tight">
              Wie X10 Unternehmen unterstützt.
            </h2>
            <p className="mt-6 text-lg text-slate-700 leading-relaxed">
              Wir analysieren Lieferketten strategisch, identifizieren Risiken und entwickeln gemeinsam belastbare
              Beschaffungs- und Produktionsstrukturen. Ein besonderer Fokus liegt auf der Entwicklung von
              Nearshoring-Strategien innerhalb Europas, der Türkei und der Schwarzmeerregion.
            </p>
          </div>

          <div className="mt-12 grid sm:grid-cols-2 gap-4">
            {X10_SUPPORT.map(({ icon: Icon, title, text }, i) => (
              <motion.div
                key={title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="p-6 rounded-2xl bg-[#0A192F] text-white border border-[#0A192F] hover:border-[#3B82F6]/50 hover:shadow-[0_0_30px_rgba(59,130,246,0.2)] transition-all duration-300"
              >
                <div className="w-11 h-11 rounded-full bg-white/10 border border-[#C9A96E]/30 flex items-center justify-center">
                  <Icon size={18} className="text-[#C9A96E]" />
                </div>
                <div className="mt-4 font-heading text-lg font-semibold">{title}</div>
                <p className="mt-2 text-sm text-slate-300 leading-relaxed">{text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom Line */}
      <section className="bg-[#F3F4F6] py-16 lg:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative p-8 lg:p-12 rounded-3xl bg-white border border-slate-200 shadow-sm">
            <div className="absolute -top-4 left-8 inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#0A192F] text-[10px] uppercase tracking-[0.3em] font-extrabold text-[#C9A96E]">
              Bottom Line
            </div>
            <h3 className="mt-2 font-heading text-2xl sm:text-3xl font-bold text-[#0A192F] tracking-tight leading-tight">
              Resiliente Lieferketten sind kein Beschaffungsthema. <span className="text-[#1E3A8A]">Sie sind
              Unternehmensstrategie.</span>
            </h3>
            <div className="mt-6 space-y-4 text-slate-700 leading-relaxed">
              <p>
                Die Wettbewerbsfähigkeit der Zukunft wird zunehmend davon abhängen, wie flexibel Unternehmen auf
                geopolitische Veränderungen reagieren, neue Märkte erschließen und ihre Lieferketten an eine sich
                wandelnde Weltwirtschaft anpassen können.
              </p>
              <p className="text-[#0A192F] font-medium">
                X10 begleitet Unternehmen auf diesem Weg – von der strategischen Analyse bis zur operativen
                Umsetzung.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0A192F] text-white py-16 lg:py-20 relative overflow-hidden">
        <div className="absolute -top-32 -left-32 w-[420px] h-[420px] rounded-full bg-[#1E3A8A]/40 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-32 -right-32 w-[420px] h-[420px] rounded-full bg-[#C9A96E]/15 blur-3xl pointer-events-none" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-extrabold text-[#C9A96E]">
            <span className="w-1.5 h-1.5 rounded-full bg-[#C9A96E]" /> Get in touch
          </span>
          <h3 className="mt-5 font-heading text-3xl sm:text-4xl font-bold tracking-tight leading-tight">
            Bereit, Ihre Lieferkette zukunftssicher aufzustellen?
          </h3>
          <p className="mt-5 text-lg text-slate-300 leading-relaxed">
            Wir prüfen mit Ihnen, welche Beschaffungs- und Produktionsstrukturen heute Risiken bergen – und entwickeln
            gemeinsam einen klaren Fahrplan für resiliente, wettbewerbsfähige Wertschöpfungsketten.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <Link
              to="/#contact"
              data-testid="sc-cta-bottom"
              className="group inline-flex items-center gap-2 px-7 py-4 rounded-full bg-white text-[#0A192F] font-semibold hover:bg-[#C9A96E] hover:text-white transition-all duration-300"
            >
              Beratungsgespräch vereinbaren
              <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
            </Link>
            <Link
              to="/#services"
              className="inline-flex items-center gap-2 px-7 py-4 rounded-full border border-white/30 font-semibold hover:bg-white/10 transition-all duration-300"
            >
              Weitere Leistungen entdecken
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
